/**
 * Links Feature - NgRx Actions
 *
 * Defines all actions for links state management using createActionGroup.
 * Actions are organized by operation type: load, selection, CRUD, and bulk operations.
 *
 * @see Requirements 13.2, 13.6
 */
import { createActionGroup, emptyProps, props } from '@ngrx/store';

import type { Link, LinkUpdatePayload, LinksListParams, LinkCreatePayload } from '../../../entities/link';
import {
  BulkDeletePayload,
  ExportLinksPayload,
  ExportQrPayload,
  LastImportStats,
  SubmitImportPayload,
} from '../model';

/**
 * Links actions grouped by feature.
 *
 * Naming convention: [Source] Event
 * - API actions: Load, Success, Failure triplets
 * - User actions: Select, Clear, Create, Update, Delete
 * - Bulk actions: Bulk Delete, Export Links, Export QR
 */
export const linksActions = createActionGroup({
  source: 'Links',
  events: {
    // ─────────────────────────────────────────────────────────────────────────
    // Load Links List
    // ─────────────────────────────────────────────────────────────────────────

    /** Load links list with current params */
    'Load Links': emptyProps(),
    /** Load links list success */
    'Load Links Success': props<{ links: Link[] }>(),
    /** Load links list failure */
    'Load Links Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Load Links Count (separate from list for optimistic UI)
    // ─────────────────────────────────────────────────────────────────────────

    /** Load total links count */
    'Load Links Count': emptyProps(),
    /** Load links count success */
    'Load Links Count Success': props<{ totalCount: number }>(),
    /** Load links count failure */
    'Load Links Count Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Query Parameters
    // ─────────────────────────────────────────────────────────────────────────

    /** Update query parameters (partial update) */
    'Update Params': props<{ params: Partial<LinksListParams> }>(),
    /** Reset query parameters to defaults */
    'Reset Params': emptyProps(),

    // ─────────────────────────────────────────────────────────────────────────
    // Selection (Single Link for Details Drawer)
    // ─────────────────────────────────────────────────────────────────────────

    /** Select a link by ID (sets selectedLinkId, may trigger load) */
    'Select Link': props<{ linkId: string }>(),
    /** Load a specific link's details */
    'Load Link': props<{ linkId: string }>(),
    /** Load link success */
    'Load Link Success': props<{ link: Link }>(),
    /** Load link failure */
    'Load Link Failure': props<{ error: string }>(),
    /** Clear selected link */
    'Clear Selected Link': emptyProps(),

    // ─────────────────────────────────────────────────────────────────────────
    // Create Link
    // ─────────────────────────────────────────────────────────────────────────

    /** Create a new link */
    'Create Link': props<{ payload: LinkCreatePayload }>(),
    /** Create link success */
    'Create Link Success': props<{ link: Link }>(),
    /** Create link failure */
    'Create Link Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Update Link
    // ─────────────────────────────────────────────────────────────────────────

    /** Update an existing link */
    'Update Link': props<{ payload: LinkUpdatePayload }>(),
    /** Update link success */
    'Update Link Success': props<{ link: Link }>(),
    /** Update link failure */
    'Update Link Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Delete Link
    // ─────────────────────────────────────────────────────────────────────────

    /** Delete a single link */
    'Delete Link': props<{ linkId: string }>(),
    /** Delete link success */
    'Delete Link Success': props<{ linkId: string }>(),
    /** Delete link failure */
    'Delete Link Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Bulk Delete
    // ─────────────────────────────────────────────────────────────────────────

    /** Bulk delete links */
    'Bulk Delete': props<{ payload: BulkDeletePayload }>(),
    /** Bulk delete success */
    'Bulk Delete Success': props<{ deletedCount: number }>(),
    /** Bulk delete failure */
    'Bulk Delete Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Export Links
    // ─────────────────────────────────────────────────────────────────────────

    /** Export links to file */
    'Export Links': props<{ payload: ExportLinksPayload }>(),
    /** Export links success */
    'Export Links Success': props<{ jobId: string }>(),
    /** Export links failure */
    'Export Links Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Export QR Codes
    // ─────────────────────────────────────────────────────────────────────────

    /** Export QR codes for links */
    'Export QR': props<{ payload: ExportQrPayload }>(),
    /** Export QR success */
    'Export QR Success': props<{ jobId: string }>(),
    /** Export QR failure */
    'Export QR Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Reset State
    // ─────────────────────────────────────────────────────────────────────────

    /** Reset links state to initial (e.g., on domain change) */
    'Reset State': emptyProps(),

    // ─────────────────────────────────────────────────────────────────────────
    // Unarchive Link
    // ─────────────────────────────────────────────────────────────────────────

    /** Unarchive a link */
    'Unarchive Link': props<{ linkId: string }>(),
    /** Unarchive link success */
    'Unarchive Link Success': props<{ link: Link }>(),
    /** Unarchive link failure */
    'Unarchive Link Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Reset Link Analytics
    // ─────────────────────────────────────────────────────────────────────────

    /** Reset link analytics */
    'Reset Link Analytics': props<{ linkId: string }>(),
    /** Reset link analytics success */
    'Reset Link Analytics Success': emptyProps(),
    /** Reset link analytics failure */
    'Reset Link Analytics Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Generate Variants
    // ─────────────────────────────────────────────────────────────────────────

    /** Generate link variants */
    'Generate Variants': props<{ linkId: string; variants: Array<Record<string, unknown>> }>(),
    /** Generate variants success */
    'Generate Variants Success': props<{ links: Link[]; count: number }>(),
    /** Generate variants failure */
    'Generate Variants Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Import Links (legacy single-step — kept for backwards compatibility)
    // ─────────────────────────────────────────────────────────────────────────

    /** Import links from file */
    'Import Links': props<{ domainId: string; formData: FormData }>(),
    /** Import links success */
    'Import Links Success': props<{ message: string; uploadId: string }>(),
    /** Import links failure */
    'Import Links Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Submit Import (two-step: createImport → dispatchImport)
    // The two-step API orchestration is an implementation detail hidden in the
    // effect. The component dispatches one action and waits for success/failure.
    // Note: File and Blob payloads are non-serializable — see SubmitImportPayload JSDoc.
    // ─────────────────────────────────────────────────────────────────────────

    /** Begin two-step import submission */
    'Submit Import': props<SubmitImportPayload>(),
    /** Import submission completed successfully */
    'Submit Import Success': emptyProps(),
    /** Import submission failed */
    'Submit Import Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Load Last Import Stats
    // No domainId on the action — the effect reads it via withLatestFrom.
    // [MEDIUM-4 resolved]
    // ─────────────────────────────────────────────────────────────────────────

    /** Load last import statistics for the current domain */
    'Load Last Import Stats': emptyProps(),
    /** Last import stats loaded successfully */
    'Load Last Import Stats Success': props<{ stats: LastImportStats }>(),
    /** Last import stats load failed */
    'Load Last Import Stats Failure': props<{ error: string }>(),

    // ─────────────────────────────────────────────────────────────────────────
    // Reset Import Status
    // Dispatched when the drawer closes to prevent stale status on re-open.
    // ─────────────────────────────────────────────────────────────────────────

    /** Reset importStatus to Idle and clear importError */
    'Reset Import Status': emptyProps(),
  },
});
